import { fieldPositions } from "@Context/game/builder/builder-context";
import { Graphics } from "@pixi/graphics";
import { Point, Point3, ScenarioPlayer } from "@Shared/types/ScenarioTypes";
import { getDistance, lerp } from "@Shared/utilities/scenarioUtilities";
import { Bezier } from "bezier-js";
import { isNumber } from "lodash-es";
import { Point as PixiPoint } from "pixi.js";
import { BuilderState, Facing, FieldSpace, Player, Waypoint } from "../models/builder-data-types";

const isValidIndex = function <T>(index: number, array: Array<T>) {
  return isNumber(index) && index >= 0 && index < array.length;
};

const lerpWaypoint = (a: Waypoint, b: Waypoint, t: number) => {
  return { position: { x: lerp(a.position.x, b.position.x, t), y: lerp(a.position.y, b.position.y, t), t: lerp(a.t, b.t, t) } };
};

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60).toFixed(0);
  let seconds = Math.floor(time % 60);
  let secondsString = seconds > 10 ? seconds.toFixed(0) : "0" + seconds.toFixed(0);
  return `${minutes}:${secondsString}`;
};

const getPointAtTime = (points: Waypoint[], t: number, beziers: Bezier[]) => {
  if (t <= points[0].t) return points[0];
  if (t >= points[points.length - 1].t) return points[points.length - 1];
  for (let i = 0; i < points.length - 1; i++) {
    if (t >= points[i].t && t <= points[i + 1].t) {
      const dt = (t - points[i].t) / (points[i + 1].t - points[i].t);

      if (beziers && beziers[i]) {
        const point = beziers[i].get(dt);
        return { position: new PixiPoint(point.x, point.y) };
      }

      return lerpWaypoint(points[i], points[i + 1], dt);
    }
  }
  return points[0];
};

const getTimeAtPoint = (points: Waypoint[], point: Point, beziers: Bezier[]) => {
  let minDistance = 1000000;
  let minTime = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const bezier = beziers[i];
    if (bezier) {
      for (let t = 0; t <= 1; t += 0.01) {
        const pointOnBezier = bezier.get(t);
        const distance = getDistance(point, pointOnBezier);
        if (distance < minDistance) {
          minDistance = distance;
          minTime = lerp(points[i].t, points[i + 1].t, t);
        }
      }
    } else {
      const point1 = points[i].position;
      const point2 = points[i + 1].position;
      const distance = getDistance(point, point1);
      if (distance < minDistance) {
        minDistance = distance;
        minTime = points[i].t;
      }
      const distance2 = getDistance(point, point2);
      if (distance2 < minDistance) {
        minDistance = distance2;
        minTime = points[i + 1].t;
      }
    }
  }
  return minTime;
};

const getBeziers = (points: Waypoint[]) => {
  const newBeziers = [];
  for (let i = 0; i < points.length - 1; i++) {
    newBeziers.push(
      new Bezier(
        points[i].position.x,
        points[i].position.y,
        points[i].next.x,
        points[i].next.y,
        points[i + 1].prev.x,
        points[i + 1].prev.y,
        points[i + 1].position.x,
        points[i + 1].position.y
      )
    );
  }
  return newBeziers;
};

const getPassFlightTime = (start: Point, end: Point, scale: number) => {
  return getDistance(start, end) / scale;
};

const getClampedPosition = (position: Point, buffer: number, screenWidth: number, screenHeight: number) => {
  return { x: Math.max(buffer, Math.min(screenWidth - buffer, position.x)), y: Math.max(buffer, Math.min(screenHeight - buffer, position.y)) };
};

const getIndexOfPlayer = (players: ScenarioPlayer[], id: number) => {
  const result = players.findIndex((player) => player.id === id);
  if (result === -1) console.log("getIndexOfPlayer", players, id);
  return result;
};

const getEndTime = (player: Player) => {
  return player.points.reduce((acc, point) => Math.max(acc, point.t), 0);
};

const getNewNext = (previousPoint: Waypoint, newPosition: Point) => {
  const dir = { x: newPosition.x - previousPoint.position.x, y: newPosition.y - previousPoint.position.y };
  const length = Math.sqrt(dir.x * dir.x + dir.y * dir.y);
  const normalised = { x: dir.x / length, y: dir.y / length };
  return { x: previousPoint.position.x + (normalised.x * length) / 4, y: previousPoint.position.y + (normalised.y * length) / 4 };
};

const round = (value: number, decimals: number) => {
  const scale = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * scale) / scale;
};

const getNearestPlayer = (players: Map<number, Player>, position: Point, time: number) => {
  let minDistance = 1000000;
  let nearest = -1;

  players.forEach((player, key) => {
    const playerPos = getPointAtTime(player.points, time, player.beziers);
    const distance = getDistance(playerPos.position, position);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = key;
    }
  });
  return nearest;
};

const drawDashedLine = (g: Graphics, x1: number, y1: number, x2: number, y2: number, dash: number, gap: number) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  const nx = dx / len;
  const ny = dy / len;
  let dist = 0;
  let on = true;
  while (dist <= len) {
    const x3 = x1 + nx * dist;
    const y3 = y1 + ny * dist;
    const x4 = x1 + nx * Math.min(dist + dash, len);
    const y4 = y1 + ny * Math.min(dist + dash, len);
    if (on) {
      g.moveTo(x3, y3);
      g.lineTo(x4, y4);
    }
    dist += gap;
    on = !on;
  }
};

const drawTemporalDashedLine = (
  g: Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  dash: number,
  gap: number,
  startTime: number,
  endTime: number,
  time: number,
  alphaScale: number,
  thickness: number,
  colour: any
) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  const nx = dx / len;
  const ny = dy / len;
  let dist = 0;
  let on = true;
  while (dist <= len) {
    const t = (dist / len) * (endTime - startTime) + startTime;
    const timeDiff = Math.min(alphaScale, Math.abs(time - t)) / alphaScale;
    const alpha = Math.max(0, 1 - timeDiff);
    g.lineStyle(thickness, colour, alpha);
    const x3 = x1 + nx * dist;
    const y3 = y1 + ny * dist;
    const x4 = x1 + nx * Math.min(dist + dash, len);
    const y4 = y1 + ny * Math.min(dist + dash, len);
    if (on) {
      g.moveTo(x3, y3);
      g.lineTo(x4, y4);
    }
    dist += gap;
    on = !on;
  }
};

const drawArrowhead = (g: Graphics, fromPos: Point, toPos: Point, size: number) => {
  const dx = toPos.x - fromPos.x;
  const dy = toPos.y - fromPos.y;
  const angle = Math.atan2(dy, dx);
  const headLength = size;
  g.moveTo(toPos.x, toPos.y);
  g.lineTo(toPos.x - headLength * Math.cos(angle - Math.PI / 6), toPos.y - headLength * Math.sin(angle - Math.PI / 6));
  g.moveTo(toPos.x, toPos.y);
  g.lineTo(toPos.x - headLength * Math.cos(angle + Math.PI / 6), toPos.y - headLength * Math.sin(angle + Math.PI / 6));
};

const createNewPoint = (previousPoint: Waypoint, newPosition: Point, time: number) => {
  const dir = { x: newPosition.x - previousPoint.position.x, y: newPosition.y - previousPoint.position.y };
  const length = Math.sqrt(dir.x * dir.x + dir.y * dir.y);
  const normalised = { x: dir.x / length, y: dir.y / length };

  return {
    position: { x: newPosition.x, y: newPosition.y },
    prev: { x: newPosition.x - (normalised.x * length) / 4, y: newPosition.y - (normalised.y * length) / 4 },
    next: { x: newPosition.x + (normalised.x * length) / 4, y: newPosition.y + (normalised.y * length) / 4 },
    t: time,
  };
};

const fieldToScreen = (fieldPoint: Point, field: FieldSpace) => {
  return {
    x: (fieldPoint.y / field.fieldDepth) * field.trimmedMapWidth + field.halfTrimmedMapWidth + field.mapPaddingX,
    y: (fieldPoint.x / field.fieldWidth) * field.trimmedMapHeight + field.halfTrimmedMapHeight + field.mapPaddingY,
  };
};

const fieldToScreen3 = (fieldPoint: Point3, field: FieldSpace) => {
  return {
    x: (fieldPoint.y / field.fieldDepth) * field.trimmedMapWidth + field.halfTrimmedMapWidth + field.mapPaddingX,
    y: (fieldPoint.x / field.fieldWidth) * field.trimmedMapHeight + field.halfTrimmedMapHeight + field.mapPaddingY,
    z: fieldPoint.z,
  };
};

const screenToField = (screenPoint: Point, field: FieldSpace) => {
  const trimmedPoint = {
    x: screenPoint.x - field.mapPaddingX,
    y: screenPoint.y - field.mapPaddingY,
  };

  return {
    x: round(((trimmedPoint.y - field.halfTrimmedMapHeight) / field.trimmedMapHeight) * field.fieldWidth, 2),
    y: round(((trimmedPoint.x - field.halfTrimmedMapWidth) / field.trimmedMapWidth) * field.fieldDepth, 2),
  };
};

const rotatePoint = (point: Point, angle: number, field: FieldSpace, flipSigns: boolean) => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const x = point.x - field.mapWidth / 2;
  const y = point.y - field.mapHeight / 2;
  let rotatedX = x * cos - y * sin;
  let rotatedY = x * sin + y * cos;

  if (flipSigns) {
    const rot = (angle / Math.PI) % 2;
    if (rot === 0.5 || rot === 1.5) {
      rotatedX = -rotatedX;
      rotatedY = -rotatedY;
    }
  }

  return { x: rotatedX, y: rotatedY };
};

const stageToScreen = (point: Point, state: BuilderState, flipSigns: boolean = false) => {
  // Use state.viewRotation to rotate the point around the center of the map
  const angle = state.viewRotation;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const x = point.x - state.field.mapWidth / 2;
  const y = point.y - state.field.mapHeight / 2;
  let rotatedX = x * cos - y * sin;
  let rotatedY = x * sin + y * cos;

  if (flipSigns) {
    const rot = (state.viewRotation / Math.PI) % 2;
    if (rot === 0.5 || rot === 1.5) {
      rotatedX = -rotatedX;
      rotatedY = -rotatedY;
    }
  }

  return {
    x: rotatedX * state.mapScale + state.viewOffset.x,
    y: rotatedY * state.mapScale + state.viewOffset.y,
  };
};

const screenToStage = (point: Point, state: BuilderState) => {
  return {
    x: (point.x - state.viewOffset.x) / state.mapScale,
    y: (point.y - state.viewOffset.y) / state.mapScale,
  };
};

const getJerseyNumber = (initials: string) => {
  const fieldPosition = fieldPositions.find((position) => position.initials === initials);
  return fieldPosition ? fieldPosition.jerseyNumber : -1;
};

const getAngleAtTime = (angles: Facing[], time: number) => {
  if (!angles || angles.length === 0) return 0;

  let index = 0;
  for (let i = 0; i < angles.length; i++) {
    if (time >= angles[i].t && (i >= angles.length - 1 || time <= angles[i + 1].t)) {
      index = i;
      break;
    }
  }

  let angle = angles[index].angle;
  if (index < angles.length - 1) {
    const fromAngle = angles[index].angle;
    const toAngle = angles[index + 1].angle;
    const fromTime = angles[index].t;
    const toTime = angles[index + 1].t;
    const timeDiff = toTime - fromTime;
    const t = (time - fromTime) / timeDiff;

    let angleDiff = toAngle - fromAngle;
    if (toAngle - fromAngle > Math.PI) angleDiff = toAngle - fromAngle - Math.PI * 2;
    else if (toAngle - fromAngle < -Math.PI) angleDiff = toAngle - fromAngle + Math.PI * 2;

    angle = fromAngle + angleDiff * t;
  }
  return angle;
};

export {
  createNewPoint,
  drawArrowhead,
  drawDashedLine,
  drawTemporalDashedLine,
  fieldToScreen,
  fieldToScreen3,
  formatTime,
  getAngleAtTime,
  getBeziers,
  getClampedPosition,
  getDistance,
  getEndTime,
  getIndexOfPlayer,
  getJerseyNumber,
  getNearestPlayer,
  getNewNext,
  getPassFlightTime,
  getPointAtTime,
  getTimeAtPoint,
  isValidIndex,
  lerp,
  lerpWaypoint,
  rotatePoint,
  round,
  screenToField,
  screenToStage,
  stageToScreen,
};
